import type { StateCreator } from 'zustand/vanilla';

// Tour 队列项接口
interface TourQueueItem {
  id: string;
  priority: number;
  ready?: boolean; // 标记组件是否准备好
}

export interface TourQueueState {
  tourQueue: TourQueueItem[];
  activeTour: TourQueueItem[];
}

export interface TourQueueStore extends TourQueueState {
  // 添加 Tour 到队列
  addToTourQueue: (tourItem: { id: string; priority?: number }) => void;
  // 标记 Tour ready状态
  markTourReady: (id: string, ready: boolean) => void;
  // 激活队列中的下一个 Tour（只激活已准备好的）
  activateNextTour: (id: string) => void;
  // 从队列中移除指定 Tour
  removeFromTourQueue: (id: string) => void;
  // 关闭当前激活的 Tour
  dismissActiveTour: (id: string) => void;
  // 清空 Tour 队列
  clearTourQueue: () => void;
  // 检查队列中是否存在某 Tour
  hasTourInQueue: (id: string) => boolean;
}

const initialState: TourQueueState = {
  tourQueue: [],
  activeTour: [],
};

export const createTourQueueSlice: StateCreator<TourQueueStore, [], [], TourQueueStore> = (set, get) => ({
  ...initialState,

  addToTourQueue: ({ id, priority }: { id: string; priority?: number }) => {
    const tourItem: TourQueueItem = {
      id,
      priority: priority || 0,
      ready: false, // 默认为未准备好
    };

    set((state) => {
      // 检查是否已经存在于队列或激活状态中
      if (get().hasTourInQueue(id)) return state;
      // 将新 Tour 添加到队列，并按优先级排序
      const newQueue = [...state.tourQueue, tourItem].sort((a, b) => b.priority - a.priority);
      return { tourQueue: newQueue };
    });
  },

  markTourReady: (id: string, ready) => {
    set((state) => ({
      tourQueue: state.tourQueue.map((item) => (item.id === id ? { ...item, ready: ready } : item)),
    }));
  },

  activateNextTour: (id: string) => {
    // 只从已准备好的 Tour 中选择
    const readyTours = get().tourQueue.filter((item) => item.ready);
    const nextTour = readyTours.find((item) => item.id === id);

    set((state) => {
      if (state.tourQueue.length === 0 || !nextTour) return state;
      return {
        tourQueue: state.tourQueue.filter((v) => v.id !== nextTour.id),
        activeTour: [...state.activeTour, nextTour],
      };
    });
  },

  removeFromTourQueue: (id) => {
    set((state) => ({
      ...state,
      tourQueue: state.tourQueue.filter((item) => item.id !== id),
      activeTour: state.activeTour.filter((item) => item.id !== id),
    }));
  },

  dismissActiveTour: (id: string) => {
    set((state) => ({
      ...state,
      activeTour: state.activeTour.filter((item) => item.id !== id),
    }));
  },

  clearTourQueue: () => {
    set({ ...initialState });
  },

  hasTourInQueue: (id) => {
    const state = get();
    return [...state.tourQueue, ...state.activeTour].some((item) => item.id === id);
  },
});
